import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { apiUrl } from "../../utils/apiUrl";
import { themeButtonBackground, themeButtonTextColor } from "../../utils/colorUtils";
import timerIcon from "../../img/timer.png";
import "../game/game.css";
import "./cocLeaderboard.css";

const TOP_N = 10;
const PUBLIC = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const TROPHY_IMG = `${PUBLIC}/img/points.png`;

function parseTimeToSeconds(t) {
  if (t == null) return 0;
  if (typeof t === "number" && Number.isFinite(t)) return Math.max(0, Math.floor(t));
  if (typeof t !== "string") return 0;
  const s = String(t).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10) || 0;
  const parts = s.split(":").map((p) => parseInt(String(p).trim(), 10) || 0);
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return 0;
}

function formatMmSs(raw) {
  const total = parseTimeToSeconds(raw);
  const m = Math.floor(total / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(m)}:${pad(s)}`;
}

function pickScore(m) {
  const keys = ["points", "score", "total_score", "spot_score"];
  for (const key of keys) {
    if (!(key in m)) continue;
    const n = parseInt(String(m[key] ?? "").trim(), 10);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function normalizeMatch(m) {
  return {
    ...m,
    userId: m.userId ?? m.id,
    name: m.name || "Anonymous",
    score: pickScore(m),
    time: m.time ?? m.reportTime ?? "00:00",
  };
}

function dedupeByUserIdBestScore(rows) {
  const map = new Map();
  for (const r of rows) {
    const id =
      r.userId != null && r.userId !== ""
        ? String(r.userId)
        : `k:${r.email || ""}:${r.name || ""}`;
    const prev = map.get(id);
    if (!prev || r.score > (prev.score ?? 0)) {
      map.set(id, r);
    } else if (prev && r.score === (prev.score ?? 0)) {
      if (parseTimeToSeconds(r.time) < parseTimeToSeconds(prev.time)) map.set(id, r);
    }
  }
  return [...map.values()];
}

function RankBadge({ rank }) {
  if (rank >= 1 && rank <= 3) {
    return (
      <img
        src={`${PUBLIC}/img/rank${rank}.png`}
        alt={`Rank ${rank}`}
        className="coc-lb-rank-img"
      />
    );
  }
  return (
    <span className="coc-lb-rank coc-lb-rank--plain" aria-label={`Rank ${rank}`}>
      {rank}
    </span>
  );
}

export default function CocLeaderboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { data: themeData, status: themeStatus } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [feedbackDone, setFeedbackDone] = useState(() => !!location.state?.feedbackSubmitted);

  const titleColor = String(themeData?.text_color || "#ffffff").trim() || "#ffffff";
  const btnBg = themeButtonBackground(themeData) || "#2563EB";
  const btnColor = themeButtonTextColor(themeData) || "#ffffff";

  useEffect(() => {
    if (!user) return;
    const base =
      (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "") || window.location.origin;
    dispatch(setBackButtonUrl(user.backButtonRedirect || base));
  }, [dispatch, user]);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user || !adminToken) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setFetchError("");
      try {
        const uid = user.userId || user.id;
        const resp = await fetch(apiUrl("/gameMatchUsers"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok || !data.success) {
          setMatches([]);
          setFetchError(data.error || data.message || "Could not load leaderboard");
          return;
        }
        let raw = Array.isArray(data.matches) ? data.matches : [];
        if (raw.length === 0 && data.currentUser) {
          const cu = data.currentUser;
          const id = cu.userId ?? cu.id;
          if (id != null && String(id).trim() !== "") {
            raw = [{ ...cu, userId: id, isSelf: true }];
          }
        }
        setMatches(dedupeByUserIdBestScore(raw.map(normalizeMatch)));
      } catch (e) {
        if (!cancelled) {
          setMatches([]);
          setFetchError(e.message || "Network error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  useEffect(() => {
    if (!user || !adminToken || feedbackDone) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId || user.id,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (data.success && data.userFeedback != null && String(data.userFeedback).trim() !== "") {
          setFeedbackDone(true);
        }
      } catch (e) {
        console.error("spotGameStatus (leaderboard)", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken, feedbackDone]);

  const ranked = useMemo(() => {
    const copy = [...matches];
    copy.sort((a, b) => {
      if ((b.score ?? 0) !== (a.score ?? 0)) return (b.score ?? 0) - (a.score ?? 0);
      return parseTimeToSeconds(a.time) - parseTimeToSeconds(b.time);
    });
    return copy.map((m, i) => ({ ...m, displayRank: i + 1 }));
  }, [matches]);

  const displayRows = useMemo(() => {
    const top = ranked.slice(0, TOP_N);
    const uid = String(user?.userId || user?.id || "");
    const me =
      ranked.find((r) => String(r.userId || "") === uid) ||
      ranked.find((r) => r.isSelf) ||
      null;
    if (me && me.displayRank > TOP_N) {
      return [...top, me];
    }
    return top;
  }, [ranked, user]);

  if (!user) return null;

  if (themeStatus === "loading" || themeStatus === "idle" || loading) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div
      className="coc-page coc-lb-page"
      style={{
        color: titleColor,
        "--coc-lb-text": titleColor,
        "--coc-lb-btn-bg": btnBg,
        "--coc-lb-btn-text": btnColor,
      }}
    >
      <div className="coc-lb-card">
        <header className="coc-lb-header">
          <img src={TROPHY_IMG} alt="" className="coc-lb-trophy" />
          <div>
            <h1 className="coc-lb-title">Leaderboard</h1>
            <p className="coc-lb-subtitle">Top players</p>
          </div>
        </header>

        <div className="coc-lb-table-head" aria-hidden>
          <span>Rank</span>
          <span>Player</span>
          <span>Score</span>
          <span>Time</span>
        </div>

        {fetchError ? <p className="coc-lb-error">{fetchError}</p> : null}

        {!fetchError && displayRows.length === 0 ? (
          <p className="coc-lb-empty">No scores yet. Be the first to finish!</p>
        ) : null}

        <ul className="coc-lb-list">
          {displayRows.map((row) => {
            const tier =
              row.displayRank === 1
                ? "gold"
                : row.displayRank === 2
                  ? "silver"
                  : row.displayRank === 3
                    ? "bronze"
                    : "plain";
            const isSelf =
              row.isSelf || String(row.userId || "") === String(user.userId || user.id || "");
            return (
              <li
                key={`${row.userId}-${row.displayRank}`}
                className={`coc-lb-row coc-lb-row--${tier}${isSelf ? " coc-lb-row--self" : ""}`}
              >
                <RankBadge rank={row.displayRank} />
                <span className="coc-lb-name">
                  {row.name}
                  {isSelf ? " (You)" : ""}
                </span>
                <span className={`coc-lb-score coc-lb-score--${tier}`}>
                  {row.score ?? 0} pts
                </span>
                <span className="coc-lb-time">
                  <img src={timerIcon} alt="" />
                  {formatMmSs(row.time)}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="coc-lb-footer">
          {feedbackDone ? (
            <p className="coc-lb-thanks">Thanks — you’ve submitted feedback for this session.</p>
          ) : (
            <Link
              to="/feedback"
              className="coc-lb-feedback"
              style={{ backgroundColor: btnBg, color: btnColor }}
            >
              <span className="coc-lb-feedback-icon" aria-hidden>
                💬
              </span>
              Feedback
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
